import { Box, Grid } from '@mui/material';
import MuiTypography from '@mui/material/Typography';
import React from 'react';

import { FieldGroup } from './FieldGroup';
import { StructureLayoutProps } from './StructureLayout.props';
import { RoleBox, ContactGridContainer } from './StructureLayout.styles';
import { SizeProps } from '../../../@types';
import { GRID_SPACING } from '../../../configs/ui-constants';
import { EMPTY_NAME_FIELD_MESSAGE } from '../../../constants/common';
import { getSiteSchemas } from '../../../utils/structure';
import { SelectMenu } from '../../atoms/SelectMenu';
import { TextField } from '../../atoms/TextField';
import { Tags } from '../Tags';

/** The same small label the address and contact rows carry. */
const PRESENTATION_LABEL_SX = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  flexShrink: 0,
};

export const StructureLayout: React.FC<StructureLayoutProps> = ({
  isNew,
  structureLayoutSchemaType,
  onChange,
  error,
  helperText,
  structureLayoutData,
  allTags,
  renderNotes = true,
  assignedSelectedTags,
  assignedNewTags,
  enableSiteSchemaType,
  enableReferenceNumbers,
  onChangeSelectedTags,
  onChangeNewTags,
  setHelperText,
  structureEditPermissions,
  isClientCustomerStructureCreation,
  selectedStructureType,
  flush,
  grouped,
}) => {
  const nameField = (
    <Grid size={{ xs: 12 }}>
      <TextField
        fullWidth
        required
        disabled={
          (!isNew && !structureEditPermissions?.hasNameEditPermission) ||
          (isClientCustomerStructureCreation &&
            (!selectedStructureType || selectedStructureType === '-1'))
        }
        error={!!helperText?.nameHelperText}
        helperText={helperText?.nameHelperText}
        label="Name"
        value={structureLayoutData.name}
        onChange={(structureName) => {
          onChange({
            ...structureLayoutData,
            name: structureName,
          });

          setHelperText({
            ...helperText,
            nameHelperText: !structureName ? EMPTY_NAME_FIELD_MESSAGE : '',
          });
        }}
      />
    </Grid>
  );

  const tagsField = (
    <Grid size={{ xs: 12 }}>
      <Tags
        selectedTags={assignedSelectedTags}
        newTags={assignedNewTags}
        setSeletedTags={onChangeSelectedTags}
        setNewTags={onChangeNewTags}
        allTags={allTags}
        disabled={
          (!isNew && !structureEditPermissions?.hasTagsEditPermission) ||
          (isClientCustomerStructureCreation &&
            (!selectedStructureType || selectedStructureType === '-1'))
        }
      />
    </Grid>
  );

  const siteSchemaField = (
    <Grid size={{ xs: 12 }}>
      <SelectMenu
        size={SizeProps.Small}
        id="page-size-menu"
        minWidth="100%"
        items={getSiteSchemas(structureLayoutSchemaType)}
        selectedValue={structureLayoutData.siteSchemaId || '-1'}
        label="Site Schema"
        onChange={(schemaId) => {
          onChange({
            ...structureLayoutData,
            siteSchemaId: schemaId,
          });
        }}
        optionalLabelEnabled={true}
      />
    </Grid>
  );

  const notesField = (
    <Grid size={{ xs: 12 }}>
      <TextField
        fullWidth
        label="Notes"
        multiline
        rows={3}
        disabled={
          (!isNew && !structureEditPermissions?.hasNotesEditPermission) ||
          (isClientCustomerStructureCreation &&
            (!selectedStructureType || selectedStructureType === '-1'))
        }
        value={structureLayoutData?.notes}
        onChange={(text) =>
          onChange({
            ...structureLayoutData,
            notes: text,
          })
        }
      />
    </Grid>
  );

  const referenceNumberFields = (
    <>
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          error={error}
          disabled={!isNew && !structureEditPermissions?.hasReference1EditPermission}
          label="Reference No 1"
          value={structureLayoutData?.referenceNo1}
          onChange={(text) =>
            onChange({
              ...structureLayoutData,
              referenceNo1: text,
            })
          }
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          error={error}
          disabled={!isNew && !structureEditPermissions?.hasReference2EditPermission}
          label="Reference No 2"
          value={structureLayoutData?.referenceNo2}
          onChange={(text) =>
            onChange({
              ...structureLayoutData,
              referenceNo2: text,
            })
          }
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          error={error}
          disabled={!isNew && !structureEditPermissions?.hasReference3EditPermission}
          label="Reference No 3"
          value={structureLayoutData?.referenceNo3}
          onChange={(text) =>
            onChange({
              ...structureLayoutData,
              referenceNo3: text,
            })
          }
        />
      </Grid>
    </>
  );

  const zoneField = (
    <Grid size={{ xs: 12 }}>
      <TextField fullWidth label="Zone" value={structureLayoutData?.zone} disabled />
    </Grid>
  );

  const regionField = (
    <Grid size={{ xs: 12 }}>
      <TextField fullWidth label="Region" value={structureLayoutData?.region} disabled />
    </Grid>
  );

  /**
   * Two read-only facts, at the density of the fields above them.
   *
   * They were 16px bold labels with 16px values — the largest type on a panel of
   * 13px inputs, for the one thing on it the user cannot change. The rating is
   * the subject and the group is already headed "Presentation", so it needs no
   * label of its own; the date is metadata about it and takes the same small
   * uppercase label the address rows use.
   */
  const presentationField = (
    <Grid size={{ xs: 12 }}>
      <MuiTypography sx={{ fontSize: '13px', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
        {structureLayoutData?.mostRecentPresentation?.description || '—'}
      </MuiTypography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.75 }}>
        <MuiTypography sx={PRESENTATION_LABEL_SX}>Assessed</MuiTypography>
        <MuiTypography sx={{ fontSize: '13px', lineHeight: 1.5 }}>
          {structureLayoutData?.mostRecentPresentation?.date || '—'}
        </MuiTypography>
      </Box>
    </Grid>
  );

  // The detail page groups the fields under eyebrows; the create page composes
  // its own `SectionGroup`s around this component, so grouping there would nest
  // one titled box inside another.
  if (grouped) {
    const hasClassification = !isClientCustomerStructureCreation || enableSiteSchemaType;

    return (
      <RoleBox flush={flush}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <FieldGroup title="Location Details">
            <ContactGridContainer container spacing={GRID_SPACING}>
              {nameField}
              {renderNotes && notesField}
            </ContactGridContainer>
          </FieldGroup>

          {hasClassification && (
            <FieldGroup title="Classification">
              <ContactGridContainer container spacing={GRID_SPACING}>
                {!isClientCustomerStructureCreation && tagsField}
                {enableSiteSchemaType && siteSchemaField}
              </ContactGridContainer>
            </FieldGroup>
          )}

          {enableReferenceNumbers && (
            <FieldGroup title="Reference numbers">
              <ContactGridContainer container spacing={GRID_SPACING}>
                {referenceNumberFields}
              </ContactGridContainer>
            </FieldGroup>
          )}

          {structureLayoutData?.mostRecentPresentation && (
            <FieldGroup title="Presentation">
              <ContactGridContainer container spacing={GRID_SPACING}>
                {presentationField}
              </ContactGridContainer>
            </FieldGroup>
          )}
        </Box>
      </RoleBox>
    );
  }

  return (
    <RoleBox flush={flush}>
      <ContactGridContainer container spacing={GRID_SPACING}>
        {nameField}
        {!isClientCustomerStructureCreation && tagsField}
        {enableSiteSchemaType && siteSchemaField}
        {renderNotes && notesField}
        {enableReferenceNumbers && referenceNumberFields}
        {!isNew && zoneField}
        {!isNew && regionField}
        {structureLayoutData?.mostRecentPresentation && presentationField}
      </ContactGridContainer>
    </RoleBox>
  );
};
